import React, { useMemo } from "react";
import { Table, Tag, Typography } from "antd";
import { pretty } from "utils/auditLogUtils";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type CreateArraySectionProps = {
  title: string;          // e.g., "feePricing"
  items: any[];           // array of objects or primitives
};

export const CreateArraySection: React.FC<CreateArraySectionProps> = ({ title, items }) => {
  const { t } = useTranslation();
  const isPrimitiveArray = useMemo(
    () => items.every((x) => x === null || ["string","number","boolean"].includes(typeof x)),
    [items]
  );

  // If primitives, render single-column table
  const primitiveColumns = [
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (v: any) => <Tag color="processing">{t(pretty("auditLog." + v))}</Tag>,
    },
  ];
  const primitiveData = items.map((v, i) => ({ key: i, value: v }));

  // If objects, infer union of keys across all rows
  const objectKeys = useMemo(() => {
    const keys = new Set<string>();
    items.forEach((it) => {
      if (it && typeof it === "object" && !Array.isArray(it)) {
        Object.keys(it).forEach((k) => keys.add(k));
      }
    });
    return Array.from(keys);
  }, [items]);

  const objectColumns = [
    { title: "#", dataIndex: "__idx", key: "__idx", width: 60 },
    ...objectKeys.map((k) => ({
      title: t("auditLog." + k) || k,
      dataIndex: k,
      key: k,
      render: (v: any) => <Tag color="processing">{pretty(v)}</Tag>,
    })),
  ];

  const objectData = items.map((row, i) => {
    if (!row || typeof row !== "object") return { __idx: i + 1, value: row, key: i };
    return { __idx: i + 1, key: i, ...row };
  });

  return (
    <div style={{ marginTop: 16 }}>
      <Text strong>{t("auditLog." + title) || title}</Text>
      <Table
        style={{ marginTop: 8 }}
        size="small"
        bordered
        rowKey="key"
        dataSource={isPrimitiveArray ? primitiveData : objectData}
        columns={isPrimitiveArray ? primitiveColumns : objectColumns}
        pagination={false}
      />
    </div>
  );
};
